<template>
  <div id="swagger-ui-container"></div> 
</template>

<script>
import { SwaggerUIBundle } from 'swagger-ui-dist';
import 'swagger-ui-dist/swagger-ui.css';
import axios from 'axios';
import yaml from 'js-yaml';
import { initializeSwaggerUIEnhancements } from '../scripts/components/swagger-ui-filter-collapse.js';

/**
 * Recursively walks an OAS document and enriches any schema property that
 * declares both `enum` and `x-enumDescriptions`.  The descriptions are appended
 * to the property's `description` field as a rendered Markdown list so that
 * Swagger UI displays them when `useUnsafeMarkdown: true` is set.
 */
function enrichEnumDescriptions(obj, visited = new Set()) {
  if (!obj || typeof obj !== 'object' || visited.has(obj)) return;
  visited.add(obj);

  if (Array.isArray(obj)) {
    obj.forEach(item => enrichEnumDescriptions(item, visited));
    return;
  }

  if (obj.enum && obj['x-enumDescriptions']) {
    const descriptions = obj['x-enumDescriptions'];
    const lines = obj.enum
      .map(val => {
        const desc = descriptions[String(val)];
        return desc ? `- \`${val}\`: ${desc}` : `- \`${val}\``;
      })
      .join('\n');
    obj.description =
      (obj.description ? obj.description + '\n\n' : '') +
      `**Enum values:**\n${lines}`;
  }

  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      enrichEnumDescriptions(obj[key], visited);
    }
  }
}

export default {
  name: 'swagger-ui-wrapper',
  props: {
    url: String
  },
  data() {
    return {
        uiRef: null,
        useOAS2: false,
        securitySchemaNames: []
    };
  },
  async mounted() {
    await this.createSwaggerUI(this.url);
  },
  beforeUnmount() {
    this.uiRef = null;
  },
  methods: {
    async createSwaggerUI(url) {

      this.uiRef = null;
      this.useOAS2 = false;
      this.securitySchemaNames = [];

      const response = await axios.get(url, {responseType: 'text', transformResponse: res => res});

      // Declare oasDoc outside try/catch so it is accessible for SwaggerUIBundle below.
      let oasDoc = null;

      try {
        oasDoc = yaml.load(response.data);

        // Parse out the names of the security schemes, so that we can pre-authorize them with the dev site's auth token
        if(oasDoc?.swagger?.startsWith('2')) {
          this.useOAS2 = true;
          if('securityDefinitions' in oasDoc) {
            for(const schemeName in oasDoc.securityDefinitions) {
              if(oasDoc.securityDefinitions[schemeName].type === 'apiKey' 
                && oasDoc.securityDefinitions[schemeName].in === 'header'
                && oasDoc.securityDefinitions[schemeName].name === "Authorization") {
                  this.securitySchemaNames.push(schemeName);
              }
            }
          }
        } else if(oasDoc?.components?.securitySchemes) {
            for(const schemeName in oasDoc.components.securitySchemes) {
              if(oasDoc.components.securitySchemes[schemeName].type === 'http' && oasDoc.components.securitySchemes[schemeName].scheme === 'bearer') {
                this.securitySchemaNames.push(schemeName);
              }
            }
        }

        // Enrich any schema property that uses x-enumDescriptions so that enum
        // values are self-documenting in the rendered Swagger UI.
        enrichEnumDescriptions(oasDoc);

      } catch(e) {
        console.log(e);
      }

      // Build SwaggerUI config:
      // • Pass `spec` (enriched object) instead of `url` — this naturally removes
      //   the raw OAS file link from the Info section (superseded by download button).
      // • `useUnsafeMarkdown: true` — Allows `style` and `class` HTML attributes
      //   through DOMPurify when rendering Markdown descriptions. Remarkable always
      //   converts **bold**/*italic* to <strong>/<em> regardless; this flag is only
      //   needed for inline HTML attributes (e.g. <span style="...">) in descriptions.
      // • Bold in plain-text fields that bypass the Markdown component is handled
      //   by the MutationObserver in swagger-ui-filter-collapse.js.
      const swaggerConfig = {
        dom_id: '#swagger-ui-container',
        useUnsafeMarkdown: true,

        onComplete: function() {
          this.uiRef = ui;

          // Defer until after React has committed the rendered spec to the real
          // DOM. When `spec:` is used (instead of `url:`), onComplete fires
          // before the paint, so querying `.opblock-tag-section` etc. would
          // find nothing and the enhancements would silently bail out.
          requestAnimationFrame(() => {
            initializeSwaggerUIEnhancements(url);
          });
        }.bind(this),

        // If we need to handle authorization declared as api parameters, use the parameterMacro,
        // check for name = "authorization" and in = "header", then set example to bearer + token
        // This only works for OAS3, and was removed because it may interfere with a basic auth scheme
        // set up the same way
      };

      // Prefer the enriched spec object; fall back to url if parsing failed.
      if (oasDoc) {
        swaggerConfig.spec = oasDoc;
      } else {
        swaggerConfig.url = url;
      }

      const ui = SwaggerUIBundle(swaggerConfig);
    },
  },
  watch: {
    url: function(newVal, oldVal) {
      if (newVal != oldVal) {
        this.createSwaggerUI(newVal)
      }
    }
  }
}
</script>

<style lang="scss">
@import '@mds/constants';
@import '@mds/typography';

/* Morningstar (M*) Look and Feel for Swagger UI */
#swagger-ui-container {

  // Force the M* font on every element inside Swagger UI.
  // The ID selector gives this rule higher specificity than all of Swagger UI's
  // own class-level font-family:sans-serif declarations, so no per-element
  // overrides are needed. Any future Swagger UI elements get the right font
  // automatically.
  .swagger-ui * {
    font-family: $mds-typography-font-family-sans-serif;
  }

  .swagger-ui {
    color: $mds-text-color-primary;

    // Monospace elements — restore after the wildcard above
    code, pre,
    .prop-enum {
      font-family: 'JetBrains Mono', monaco, 'Bitstream Vera Sans Mono', monospace;
    }

    // Markdown bold/italic rendering inside descriptions.
    // Use font-weight: bold (700) — not $mds-typography-font-weight-bold (500) —
    // so that **bold** markdown is visually distinct from the 300-weight base text.
    .renderedMarkdown strong,
    .renderedMarkdown b,
    .markdown strong,
    .markdown b,
    strong, b {
      font-weight: bold;
      color: $mds-text-color-primary;
    }

    // Operation tag group headings
    .opblock-tag {
      color: $mds-text-color-primary;
      border-bottom-color: $mds-color-neutral-20;
    }

    // Authorize button — M* interactive style
    .auth-wrapper .authorize,
    .btn.authorize {
      border-color: $mds-text-color-interactive-primary;
      color: $mds-text-color-interactive-primary;
      font-weight: $mds-typography-font-weight-bold;

      svg { fill: $mds-text-color-interactive-primary; }

      &:hover {
        background-color: $mds-text-color-interactive-primary;
        color: $mds-color-white;
        svg { fill: $mds-color-white; }
      }
    }

    // Execute (Send Request) button
    .btn.execute {
      background-color: $mds-text-color-interactive-primary;
      border-color: $mds-text-color-interactive-primary;
      color: $mds-color-white;

      &:hover { opacity: 0.85; }
    }

    // Try it out button
    .try-out__btn {
      border-color: $mds-text-color-interactive-primary;
      color: $mds-text-color-interactive-primary;

      &:hover {
        background-color: $mds-text-color-interactive-primary;
        color: $mds-color-white;
      }
    }

    // Servers dropdown
    .servers > label select {
      border-color: $mds-color-neutral-80;
      color: $mds-text-color-primary;
    }

    .servers-title,
    .scheme-container label {
      color: $mds-text-color-secondary;
      font-weight: $mds-typography-font-weight-bold;
    }
  }

  /* Filter container and input styles */
  .swagger-filter-container {
    position: relative;
    display: inline-block;

    input {
      margin: 10px;
      width: 400px;
      padding-right: 24px;
    }

    .swagger-clear-btn {
      position: absolute;
      right: 18px;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
      font-size: 18px;
      color: #888;
      display: none;
      user-select: none;
    }
  }

}
</style>